import React, { useState, useEffect, useRef } from 'react';
import { ComprehensiveEnergyData } from './types/energyData';
import { EnergyDataService } from './services/energyDataService';
import { apiService } from './services/apiService';
import { convertDashboardDataToEnergyData } from './services/dashboardDataConverter';
import ChatInterface from './components/ChatInterface';
import { EnhancedEnergyDashboard } from './components/EnhancedEnergyDashboard';
import Login from './components/Login';
import SiteSelector from './components/SiteSelector';
import { MessageCircle, Menu, X, Globe, LogOut, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext';
import './i18n';


const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, userInfo, selectedSite, logout, loading: authLoading } = useAuth();
  const [energyDataService, setEnergyDataService] = useState<EnergyDataService | null>(null);
  const [comprehensiveData, setComprehensiveData] = useState<ComprehensiveEnergyData | null>(null);
  const [activeTab, setActiveTab] = useState<'chat' | 'dashboard' | 'enhanced'>('chat');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [dashboardLoadState, setDashboardLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const dashboardLoadedSiteRef = useRef<string | null>(null);
  const dashboardInFlightRef = useRef<Set<string>>(new Set());

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    // Save to localStorage to persist user selection
    localStorage.setItem('i18nextLng', lng);
  };

  useEffect(() => {
    let cancelled = false;

    const initializeApp = async () => {
      // Only load data if authenticated and site is selected
      if (!isAuthenticated || !selectedSite) {
        setEnergyDataService(null);
        setComprehensiveData(null);
        setDashboardLoadState('idle');
        dashboardLoadedSiteRef.current = null;
        return;
      }

      // Load dashboard data only when user opens the enhanced dashboard tab.
      if (activeTab !== 'enhanced') {
        return;
      }

      // Avoid duplicate fetches in React StrictMode and repeated tab toggles.
      const requestKey = `${selectedSite}:initial-dashboard`;
      if (dashboardInFlightRef.current.has(requestKey)) {
        return;
      }
      if (
        dashboardLoadedSiteRef.current === selectedSite &&
        energyDataService &&
        comprehensiveData
      ) {
        setDashboardLoadState('ready');
        return;
      }

      dashboardInFlightRef.current.add(requestKey);

      try {
        setDashboardLoadState('loading');

        // Stage 1: fast first paint with 24h aggregated data
        console.log(`Loading dashboard data for site: ${selectedSite}`);
        const dashboardData = await apiService.getDashboardData(selectedSite, 24, true);
        if (cancelled) return;

        // Convert API response to EnergyData format
        const energyData = convertDashboardDataToEnergyData(dashboardData);

        if (energyData.length === 0) {
          console.warn('No data received from API for site:', selectedSite);
          setEnergyDataService(null);
          setComprehensiveData(null);
          setDashboardLoadState('error');
          return;
        }

        // Create EnergyDataService with converted data
        const service = new EnergyDataService(energyData);
        const comprehensiveEnergyData = service.getComprehensiveEnergyData();

        setEnergyDataService(service);
        setComprehensiveData(comprehensiveEnergyData);
        setDashboardLoadState('ready');
        dashboardLoadedSiteRef.current = selectedSite;
        console.log(`Loaded ${energyData.length} sensor groups for site: ${selectedSite}`);

        // Stage 2: background enrichment with 72h aggregated data (non-blocking)
        try {
          const enrichedData = await apiService.getDashboardData(selectedSite, 72, true);
          if (cancelled) return;

          const enrichedEnergyData = convertDashboardDataToEnergyData(enrichedData);
          if (enrichedEnergyData.length > 0) {
            const enrichedService = new EnergyDataService(enrichedEnergyData);
            const enrichedComprehensiveData = enrichedService.getComprehensiveEnergyData();
            setEnergyDataService(enrichedService);
            setComprehensiveData(enrichedComprehensiveData);
            console.log(`Enriched dashboard with ${enrichedEnergyData.length} sensor groups for site: ${selectedSite}`);
          }
        } catch (enrichmentError) {
          // Keep the fast 24h render if enrichment fails.
          console.warn('Dashboard enrichment failed, keeping initial dataset:', enrichmentError);
        }
      } catch (error) {
        console.error('Error loading energy data from API:', error);
        // Set empty data so UI can still render
        setEnergyDataService(null);
        setComprehensiveData(null);
        setDashboardLoadState('error');
        // Don't throw - allow user to see UI even if data fails to load
      } finally {
        dashboardInFlightRef.current.delete(requestKey);
      }
    };

    initializeApp();
    return () => {
      cancelled = true;
    };
  }, [activeTab, isAuthenticated, selectedSite, energyDataService, comprehensiveData]);

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">{t('common.loading')}</h2>
          <p className="text-gray-500 mt-2">{t('header.subtitle')}</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // Always show header when authenticated - don't block UI on data loading errors
  // Show loading/error states in main content area instead

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 safe-area-top sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <img 
                src="/logo.svg" 
                alt="Soleco Logo" 
                className="w-12 h-8 sm:w-16 sm:h-10"
              />
              <div>
                <h1 className="text-base sm:text-lg font-bold text-gray-900">{t('header.title')}</h1>
                <p className="text-[10px] sm:text-xs text-gray-600 hidden sm:block">{t('header.subtitle')}</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-4">
              {/* Site Selector */}
              {userInfo && userInfo.sites.length > 0 && (
                <div className="mr-2">
                  <SiteSelector />
                </div>
              )}
              
              {/* Dashboard Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveTab('enhanced')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200 flex items-center gap-2 ${
                    activeTab === 'enhanced'
                      ? 'bg-soleco-500 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 bg-gray-50'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  {t('header.enhancedDashboard')}
                </button>
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200 flex items-center gap-2 ${
                    activeTab === 'chat'
                      ? 'bg-soleco-500 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 bg-gray-50'
                  }`}
                >
                  <MessageCircle className="w-4 h-4" />
                  ChatBot
                </button>
              </div>
              
              {/* Language Switcher */}
              <div className="flex items-center gap-2 ml-2 pl-4 border-l border-gray-300">
                <Globe className="w-4 h-4 text-gray-500" />
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    i18n.language === 'en' 
                      ? 'bg-soleco-500 text-white shadow-sm' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => changeLanguage('de')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    i18n.language === 'de' 
                      ? 'bg-soleco-500 text-white shadow-sm' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  DE
                </button>
              </div>
              
              {/* User Info and Logout */}
              <div className="flex items-center gap-3 ml-2 pl-4 border-l border-gray-300">
                <span className="text-sm font-medium text-gray-700">{userInfo?.username || 'User'}</span>
                <button
                  onClick={logout}
                  className="p-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200 flex items-center justify-center shadow-sm hover:shadow-md"
                  title={t('header.logout')}
                  aria-label={t('header.logout')}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 active:bg-gray-200 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 safe-area-bottom">
              <nav className="flex flex-col gap-2">
                {/* Site Selector */}
                {userInfo && userInfo.sites.length > 0 && (
                  <div className="px-4 py-2">
                    <SiteSelector />
                  </div>
                )}
                
                <button
                  onClick={() => {
                    setActiveTab('enhanced');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`px-4 py-3.5 rounded-lg font-medium transition-colors duration-200 min-h-[44px] flex items-center touch-manipulation ${
                    activeTab === 'enhanced'
                      ? 'bg-soleco-500 text-white'
                      : 'text-gray-700 active:bg-gray-100'
                  }`}
                >
                  <BarChart3 className="w-4 h-4 inline mr-2" />
                  {t('header.enhancedDashboard')}
                </button>
                <button
                  onClick={() => {
                    setActiveTab('chat');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`px-4 py-3.5 rounded-lg font-medium transition-colors duration-200 min-h-[44px] flex items-center touch-manipulation ${
                    activeTab === 'chat'
                      ? 'bg-soleco-500 text-white'
                      : 'text-gray-700 active:bg-gray-100'
                  }`}
                >
                  <MessageCircle className="w-4 h-4 inline mr-2" />
                  ChatBot
                </button>
                
                {/* Mobile Language Switcher */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-200 px-4">
                  <Globe className="w-4 h-4 text-gray-500" />
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`px-3 py-2 text-sm rounded ${
                      i18n.language === 'en' ? 'bg-soleco-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('de')}
                    className={`px-3 py-2 text-sm rounded ${
                      i18n.language === 'de' ? 'bg-soleco-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Deutsch
                  </button>
                </div>
                
                {/* Mobile User Info and Logout */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-200 px-4">
                  <span className="text-sm font-medium text-gray-700">{userInfo?.username || 'User'}</span>
                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="p-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200 flex items-center justify-center shadow-sm"
                    title={t('header.logout')}
                    aria-label={t('header.logout')}
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className={activeTab === 'chat' ? 'h-[calc(100svh-4rem)] sm:h-[calc(100vh-4rem)]' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {activeTab === 'chat' ? (
          <div className="h-full">
            {/* ChatInterface doesn't need energyDataService - render immediately */}
            <ChatInterface 
              energyDataService={energyDataService} 
              siteId={selectedSite || ''} 
            />
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            {activeTab === 'enhanced' && dashboardLoadState === 'loading' && (
              <div className="min-h-[50vh] flex items-center justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-soleco-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading dashboard data...</p>
                </div>
              </div>
            )}

            {activeTab === 'enhanced' && dashboardLoadState !== 'loading' && comprehensiveData && energyDataService && (
              <EnhancedEnergyDashboard data={comprehensiveData} energyDataService={energyDataService} />
            )}

            {activeTab === 'enhanced' && dashboardLoadState === 'error' && (
              <div className="min-h-[40vh] flex items-center justify-center">
                <div className="text-center max-w-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Dashboard data failed to load</h3>
                  <p className="text-gray-600">Please retry in a few seconds or switch sites and come back.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Floating Chat Button - only show when not on chat tab, hide on mobile */}
      {activeTab !== 'chat' && (
        <button
          onClick={() => setActiveTab('chat')}
          className="hidden sm:flex fixed bottom-6 right-6 w-14 h-14 bg-soleco-500 hover:bg-soleco-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 items-center justify-center z-50 animate-pulse hover:animate-none touch-manipulation"
          aria-label={t('common.chat')}
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

    </div>
  );
};

export default App;
